import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { BaseDialog } from '../shared/dialogs/base-dialog';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SharedUpdateService } from '../shared/dialogs/update.service';
import { ILocalizationDictionary, ITranslation, LanguageCode } from './interfaces';
import { Subject } from 'rxjs';
import { FormBuilder, Validators } from '@angular/forms';
import { TranslateService } from './service';
import { NotificationService } from '../../services/notification.service';
import { eNotificationTypes } from '../../interfaces/constants';

export interface ILocalizedDictionaryDetailsDialog {
	translation: ITranslation,
	languageCodes: LanguageCode[]
}

export type LocalizedDictionaryDetailsFields = 'Keyword' | 'LocalizedDictionaries';


@Component({
	moduleId: module.id,
	selector: 'localized-dictionary-details',
	templateUrl: 'localization-details.html',
})

export class LocalizedDictionaryDetails extends BaseDialog implements OnInit, OnDestroy {

	public isEditMode: boolean;
	public languageCodes: LanguageCode[];

	protected _subscriptions$: Subject<boolean>;
	private _translation: ITranslation;

	public get f(): { [key in LocalizedDictionaryDetailsFields]: AbstractControl } {
		if (!this.form) {
			return null
		};

		return this.form.controls as { [key in LocalizedDictionaryDetailsFields]: AbstractControl };
	}

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: ILocalizedDictionaryDetailsDialog,
		private dialog: MatDialog,
		private dialogRef: MatDialogRef<LocalizedDictionaryDetails>,
		private _fb: FormBuilder,
		private _notificationService: NotificationService,
		private sharedUpdateService: SharedUpdateService,
		private translateService: TranslateService,
	) {
		super(dialog, dialogRef, sharedUpdateService);

		if (!data) {
			throw new Error('data for localizedDictionaryDetails missing');
		}

		this._translation = this.data.translation;
		this.languageCodes = this.data.languageCodes;
		this.isEditMode = false;
	}

	ngOnInit(): void {
		// when creating, initialize with empty values
		this._initializeForm(this._translation);
		this._determineEditMode();
	}

	ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	private _determineEditMode(): void {

		for (let languageCode of this.languageCodes) {
			if (!this._translation.translations ||
				!this._translation.translations[languageCode.Id] ||
				!this._translation.translations[languageCode.Id].Id) {

				this.isEditMode = false;
				return;
			}
		}

		this.isEditMode = true;
	}

	public getDictionaryFormGroup(LanguageCodeId: number): FormGroup {
		let result: FormGroup;

		result = (this.f.LocalizedDictionaries as FormArray).controls.find(x =>
			x.get('LanguageCode').value === LanguageCodeId) as FormGroup;

		return result;
	}

	private _initializeForm(translation: ITranslation): void {
		// translate this back into a form that can be posted to server, since this form
		// holds minimal information

		this.form = this._fb.group({
			Keyword: [translation.keyword, Validators.required],
			LocalizedDictionaries: this._fb.array([])
		});

		for (let languageCode of this.languageCodes) {
			(this.f.LocalizedDictionaries as FormArray).controls.push(
				this._getLocalizedDictionaryFormGroup(
					translation.translations[languageCode.Id], languageCode.Id));
		}
	}

	private _getLocalizedDictionaryFormGroup(localizedDictionary:
		ILocalizationDictionary, languageCodeId: number): FormGroup {

		let result: FormGroup;

		result = this._fb.group({
			Id: [localizedDictionary ? localizedDictionary.Id : null],
			LanguageCode: [languageCodeId, Validators.required],
			Word: [localizedDictionary ? localizedDictionary.Word : null]
		});

		return result;
	}

	/**
	 * close the dialog
	 * trigger a keep/discard
	 */
	public cancel(): void {
		super.close();
	}

	/**
	 * translate the form into a structure that can be saved
	 */
	public save(): void {
		let promises: Promise<any>[] = [];

		for (let languageCode of this.languageCodes) {
			let localizedDictionaryFormGroup: ILocalizationDictionary;
			let promise: Promise<void>;

			localizedDictionaryFormGroup = this.getDictionaryFormGroup(languageCode.Id).value as ILocalizationDictionary;

			if (!localizedDictionaryFormGroup.Id) {
				// create
				promise = this.translateService.addTranslation(this.f.Keyword.value,
					[{ languageCode: languageCode.Id, word: localizedDictionaryFormGroup.Word}])
					.then((errors) => {

					if (errors && errors.length > 0) {
						this._notificationService.generateNotifcation({
							type: eNotificationTypes.ERROR,
							message: errors
						});
					}
				});

				promises.push(promise);
			} else {
				// update
				promise = this.translateService.saveTranslation([{
					Keyword: this.f.Keyword.value,
					Id: localizedDictionaryFormGroup.Id,
					LanguageCode: languageCode.Id,
					Word: localizedDictionaryFormGroup.Word
					}]).then(() => {
				})

				promises.push(promise);
			}
		}

		Promise.all(promises).then(() => {
			this.sharedUpdateService.saveComplete();
			this.close();
		});
	}
}
